import { useState, useEffect } from "react";
import useAPI from "../hooks/useAPI";

export default function useAdminCustomersStats() {
  const [periodType, setPeriodType] = useState("30days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    periodType: "30days",
    startDate: null,
    endDate: null,
  });
  const [stats, setStats] = useState({
    totalCustomers: 0,
    byDate: [],
    trend: 0,
    averagePerDay: 0,
  });

  // Charger tous les clients une seule fois
  const { data, query: fetchData } = useAPI({
    type: "get",
    url: "customers",
    defer: false,
  });

  // Debug: afficher la structure des données reçues
  useEffect(() => {
    if (data) {
      console.log("👥 Données reçues de l'API customers:", data);
      console.log("👥 Structure:", Object.keys(data));
      if (data.items && data.items.length > 0) {
        console.log("👥 Premier client:", data.items[0]);
        console.log("👥 Champs disponibles:", Object.keys(data.items[0]));
      } else if (data.data && data.data.length > 0) {
        console.log("👥 Premier client:", data.data[0]);
        console.log("👥 Champs disponibles:", Object.keys(data.data[0]));
      }
    }
  }, [data]);

  // Calculer les dates selon le type de période
  const calculateDates = (type) => {
    const end = new Date();
    const start = new Date();

    switch (type) {
      case "7days":
        start.setDate(end.getDate() - 6);
        break;
      case "30days":
        start.setDate(end.getDate() - 29);
        break;
      case "3months":
        start.setMonth(end.getMonth() - 3);
        break;
      case "year":
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        break;
    }

    return { start, end };
  };

  // Calculer les statistiques à partir des clients filtrés
  const calculateStats = (customers) => {
    console.log("🔍 calculateStats appelé avec", customers.length, "clients");
    
    if (customers.length > 0) {
      console.log("🔍 Premier client:", customers[0]);
      console.log("🔍 Champs du premier client:", Object.keys(customers[0]));
    }
    
    if (!customers || !appliedFilters.startDate || !appliedFilters.endDate) {
      return {
        totalCustomers: 0,
        byDate: [],
        trend: 0,
        averagePerDay: 0,
      };
    }

    // Filtrer les clients par date
    const startTime = new Date(appliedFilters.startDate);
    startTime.setHours(0, 0, 0, 0); // Début de la journée
    const endTime = new Date(appliedFilters.endDate);
    endTime.setHours(23, 59, 59, 999); // Fin de la journée

    console.log("🔍 Période de filtrage:", {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString()
    });

    const filteredCustomers = customers.filter((customer, index) => {
      const customerDate = new Date(customer.userCreated);
      
      // Debug pour les 3 premiers clients
      if (index < 3) {
        console.log(`🔍 Client ${index}:`, {
          userCreated: customer.userCreated,
          parsedDate: customerDate,
          isValid: !isNaN(customerDate.getTime()),
          isInRange: customerDate >= startTime && customerDate <= endTime
        });
      }
      
      return customerDate >= startTime && customerDate <= endTime;
    });

    // Total des clients
    const totalCustomers = filteredCustomers.length;

    // Grouper par date
    const byDateMap = {};
    filteredCustomers.forEach((customer) => {
      const date = new Date(customer.userCreated)
        .toISOString()
        .split("T")[0];
      if (!byDateMap[date]) {
        byDateMap[date] = { count: 0 };
      }
      byDateMap[date].count++;
    });

    const byDate = Object.entries(byDateMap)
      .map(([date, data]) => ({
        date,
        count: data.count,
      }))
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    // Calculer la tendance (différence avec période précédente)
    const periodDays = Math.ceil((endTime - startTime) / (1000 * 60 * 60 * 24));
    const previousStart = new Date(startTime);
    previousStart.setDate(previousStart.getDate() - periodDays);
    const previousEnd = new Date(startTime);
    previousEnd.setDate(previousEnd.getDate() - 1);

    const previousCustomers = customers.filter((customer) => {
      const customerDate = new Date(customer.userCreated);
      return customerDate >= previousStart && customerDate <= previousEnd;
    });

    const trend =
      previousCustomers.length > 0
        ? ((totalCustomers - previousCustomers.length) / previousCustomers.length) * 100
        : 0;

    // Moyenne par jour
    const averagePerDay = periodDays > 0 ? totalCustomers / periodDays : 0;

    return {
      totalCustomers,
      byDate,
      trend,
      averagePerDay,
    };
  };

  // Initialiser les dates au montage
  useEffect(() => {
    const dates = calculateDates(periodType);
    setStartDate(dates.start);
    setEndDate(dates.end);
    setAppliedFilters({
      periodType,
      startDate: dates.start,
      endDate: dates.end,
    });
  }, []);

  // Recalculer les stats quand les données ou les filtres changent
  useEffect(() => {
    console.log("🔄 useEffect clients - data:", data);
    console.log("🔄 Filtres appliqués:", appliedFilters);

    if (data) {
      // Essayer différentes structures possibles
      let customersData = null;
      if (data.items && Array.isArray(data.items)) {
        console.log("✅ Trouvé data.items:", data.items.length, "clients");
        customersData = data.items;
      } else if (data.data && Array.isArray(data.data)) {
        console.log("✅ Trouvé data.data:", data.data.length, "clients");
        customersData = data.data;
      } else if (Array.isArray(data)) {
        console.log(
          "✅ data est un tableau direct:",
          data.length,
          "clients"
        );
        customersData = data;
      } else if (data.customers) {
        console.log(
          "✅ Trouvé data.customers:",
          data.customers.length,
          "clients"
        );
        customersData = data.customers;
      } else {
        console.log(
          "❌ Structure inconnue - Clés disponibles:",
          Object.keys(data)
        );
      }

      if (customersData) {
        const newStats = calculateStats(customersData);
        console.log("📊 Stats clients calculées:", newStats);
        setStats(newStats);
      }
    } else {
      console.log("❌ Pas de données clients disponibles");
    }
  }, [data, appliedFilters]);

  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    if (type !== "custom") {
      const dates = calculateDates(type);
      setStartDate(dates.start);
      setEndDate(dates.end);
    } else {
      // En mode custom, s'assurer que les dates sont définies
      if (!startDate || !endDate) {
        const dates = calculateDates("30days");
        setStartDate(dates.start);
        setEndDate(dates.end);
      }
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters({
      periodType,
      startDate,
      endDate,
    });
  };

  const handleRefresh = async () => {
    await fetchData();
  };

  return {
    periodType,
    startDate,
    endDate,
    stats,
    loading: !data,
    handlePeriodTypeChange,
    handleStartDateChange: setStartDate,
    handleEndDateChange: setEndDate,
    handleApplyFilters,
    handleRefresh,
  };
}
